import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { api, query } from '../../api/client'
import type { AppDetail, RankSeries } from '../../api/types'
import { SERIES_COLORS, seriesLabel } from '../../lib/rank-series'
import { RangePicker, rangeFrom, type RangeKey } from '../RangePicker'
import { RankChart } from './RankChart'

const DEFAULT_VISIBLE = 5

/** Rank history for the charts where the app ranks best, with version releases marked. */
export function RankPanel({ app }: { app: AppDetail }) {
	const [range, setRange] = useState<RangeKey>('30d')
	const [storefront, setStorefront] = useState('')
	const [hidden, setHidden] = useState<Set<number>>(new Set())
	const from = rangeFrom(range)
	const ranks = useQuery({
		queryKey: ['ranks', app.id, range, storefront],
		queryFn: () => api.get<RankSeries[]>(`/api/v1/apps/${app.id}/ranks${query({ from, storefront, charts: 20 })}`),
	})
	const series = ranks.data ?? []
	const storefronts = [...new Set(series.map((s) => s.chart.storefront))].sort()
	const visible = series.filter((s, i) => (hidden.size ? !hidden.has(s.chart.id) : i < DEFAULT_VISIBLE))
	const colorFor = (s: RankSeries) => SERIES_COLORS[series.indexOf(s) % SERIES_COLORS.length]
	const markers = app.versions.filter((v) => v.releasedAt).map((v) => ({ at: v.releasedAt!, label: `v${v.version}` }))

	function toggle(id: number) {
		const base = hidden.size ? hidden : new Set(series.slice(DEFAULT_VISIBLE).map((s) => s.chart.id))
		const next = new Set(base)
		if (next.has(id)) next.delete(id)
		else next.add(id)
		setHidden(next.size === series.length ? new Set() : next)
	}

	return (
		<section className="card stack">
			<div className="row" style={{ justifyContent: 'space-between' }}>
				<h2 style={{ margin: 0 }}>Rank history</h2>
				<div className="row">
					<select aria-label="Rank country" value={storefront} onChange={(e) => setStorefront(e.target.value)}>
						<option value="">All countries</option>
						{(storefront ? [storefront] : storefronts).map((s) => (
							<option key={s} value={s}>
								{s.toUpperCase()}
							</option>
						))}
					</select>
					<RangePicker value={range} onChange={setRange} />
				</div>
			</div>
			{ranks.isPending ? (
				<div className="page-loading">Loading…</div>
			) : series.length === 0 ? (
				<div className="empty">Not ranked in this period.</div>
			) : (
				<RankChart series={visible} labelFor={(s) => seriesLabel(s)} colorFor={colorFor} markers={markers} />
			)}
			{series.length > 0 && (
				<div className="row small">
					{series.map((s) => {
						const on = visible.includes(s)
						return (
							<button
								key={s.chart.id}
								type="button"
								aria-pressed={on}
								onClick={() => toggle(s.chart.id)}
								style={{ opacity: on ? 1 : 0.55 }}
							>
								<span
									aria-hidden
									style={{
										display: 'inline-block',
										width: 10,
										height: 10,
										borderRadius: 5,
										marginRight: 6,
										background: colorFor(s),
									}}
								/>
								{seriesLabel(s)} · best #{s.bestRank}
							</button>
						)
					})}
				</div>
			)}
		</section>
	)
}
